import React from 'react';
import { Form, Input, InputNumber, Switch, Card, Typography, Row, Col, Select, Divider } from 'antd';

const { Title, Text } = Typography;
const { Option } = Select;

export interface CustomerTypeConfigProps {
    customerType: 'INDIVIDUAL' | 'GROUP' | 'BUSINESS';
    groupConfig?: any;
    businessConfig?: any;
    uiConfiguration?: any;
    onGroupConfigChange?: (config: any) => void;
    onBusinessConfigChange?: (config: any) => void;
    onUIConfigurationChange?: (config: any) => void;
    disabled?: boolean;
}

const CustomerTypeConfigForm: React.FC<CustomerTypeConfigProps> = ({
    customerType,
    groupConfig,
    businessConfig,
    uiConfiguration,
    onGroupConfigChange,
    onBusinessConfigChange,
    onUIConfigurationChange,
    disabled = false
}) => {
    const handleGroupConfigChange = (field: string, value: any) => {
        const updatedConfig = { ...groupConfig, [field]: value };
        onGroupConfigChange?.(updatedConfig);
    };

    const handleBusinessConfigChange = (field: string, value: any) => {
        const updatedConfig = { ...businessConfig, [field]: value };
        onBusinessConfigChange?.(updatedConfig);
    };

    const handleUIConfigurationChange = (field: string, value: any) => {
        const updatedConfig = { ...uiConfiguration, [field]: value };
        onUIConfigurationChange?.(updatedConfig);
    };

    // UI Configuration Section (displayed for all customer types)
    const renderUIConfigurationSection = () => (
        <Card style={{ marginBottom: 16 }}>
            <Title level={4}>UI Configuration</Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                Configure how the customer category form will be displayed and behave.
            </Text>

            <Row gutter={[16, 16]}>
                <Col span={12}>
                    <Form.Item label="Rendering Mode">
                        <Select
                            value={uiConfiguration?.renderingMode || 'tabs'}
                            onChange={(value) => handleUIConfigurationChange('renderingMode', value)}
                            disabled={disabled}
                            placeholder="Select rendering mode"
                        >
                            <Option value="tabs">Tabs</Option>
                            <Option value="wizard">Step Wizard</Option>
                            <Option value="accordion">Accordion</Option>
                        </Select>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
                            How field groups are displayed to users
                        </Text>
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Wizard Orientation">
                        <Select
                            value={uiConfiguration?.wizardOrientation || 'horizontal'}
                            onChange={(value) => handleUIConfigurationChange('wizardOrientation', value)}
                            disabled={disabled || uiConfiguration?.renderingMode !== 'wizard'}
                            placeholder="Select orientation"
                        >
                            <Option value="horizontal">Horizontal</Option>
                            <Option value="vertical">Vertical</Option>
                        </Select>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
                            Wizard step layout direction
                        </Text>
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col span={8}>
                    <Form.Item label="Show Progress" valuePropName="checked">
                        <Switch
                            checked={uiConfiguration?.showProgress || false}
                            onChange={(checked) => handleUIConfigurationChange('showProgress', checked)}
                            disabled={disabled}
                        />
                        <Text type="secondary" style={{ marginLeft: 8, fontSize: '12px' }}>
                            Display progress indicator
                        </Text>
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="Show Group Navigation" valuePropName="checked">
                        <Switch
                            checked={uiConfiguration?.showGroupNavigation || true}
                            onChange={(checked) => handleUIConfigurationChange('showGroupNavigation', checked)}
                            disabled={disabled}
                        />
                        <Text type="secondary" style={{ marginLeft: 8, fontSize: '12px' }}>
                            Show navigation between groups
                        </Text>
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="Validate All Groups" valuePropName="checked">
                        <Switch
                            checked={uiConfiguration?.validateAllGroups || true}
                            onChange={(checked) => handleUIConfigurationChange('validateAllGroups', checked)}
                            disabled={disabled}
                        />
                        <Text type="secondary" style={{ marginLeft: 8, fontSize: '12px' }}>
                            Validate all groups on submit
                        </Text>
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col span={8}>
                    <Form.Item label="Allow Group Collapse" valuePropName="checked">
                        <Switch
                            checked={uiConfiguration?.allowGroupCollapse || false}
                            onChange={(checked) => handleUIConfigurationChange('allowGroupCollapse', checked)}
                            disabled={disabled}
                        />
                        <Text type="secondary" style={{ marginLeft: 8, fontSize: '12px' }}>
                            Allow collapsing field groups
                        </Text>
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="Show Field Count" valuePropName="checked">
                        <Switch
                            checked={uiConfiguration?.showFieldCount || true}
                            onChange={(checked) => handleUIConfigurationChange('showFieldCount', checked)}
                            disabled={disabled}
                        />
                        <Text type="secondary" style={{ marginLeft: 8, fontSize: '12px' }}>
                            Display field count in groups
                        </Text>
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="Auto Save" valuePropName="checked">
                        <Switch
                            checked={uiConfiguration?.autoSave || false}
                            onChange={(checked) => handleUIConfigurationChange('autoSave', checked)}
                            disabled={disabled}
                        />
                        <Text type="secondary" style={{ marginLeft: 8, fontSize: '12px' }}>
                            Auto-save form changes
                        </Text>
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col span={12}>
                    <Form.Item label="Theme">
                        <Select
                            value={uiConfiguration?.theme || 'default'}
                            onChange={(value) => handleUIConfigurationChange('theme', value)}
                            disabled={disabled}
                            placeholder="Select theme"
                        >
                            <Option value="default">Default</Option>
                            <Option value="compact">Compact</Option>
                            <Option value="spacious">Spacious</Option>
                        </Select>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
                            Visual theme for the form
                        </Text>
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Validation Mode">
                        <Select
                            value={uiConfiguration?.validationMode || 'onChange'}
                            onChange={(value) => handleUIConfigurationChange('validationMode', value)}
                            disabled={disabled}
                            placeholder="Select validation mode"
                        >
                            <Option value="onChange">On Change</Option>
                            <Option value="onBlur">On Blur</Option>
                            <Option value="onSubmit">On Submit</Option>
                        </Select>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
                            When to trigger field validation
                        </Text>
                    </Form.Item>
                </Col>
            </Row>
        </Card>
    );

    if (customerType === 'INDIVIDUAL') {
        return (
            <>
                {renderUIConfigurationSection()}
                <Card>
                    <Title level={4}>Individual Customer Configuration</Title>
                    <Text type="secondary">
                        Individual customers don't require additional configuration. 
                        Standard customer onboarding fields will be used.
                    </Text>
                </Card>
            </>
        );
    }

    if (customerType === 'GROUP') {
        return (
            <>
                {renderUIConfigurationSection()}
                <Card>
                <Title level={4}>Group Customer Configuration</Title>
                <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                    Configure requirements for group customers such as investment groups, chamas, etc.
                </Text>
                
                <Row gutter={[16, 16]}>
                    <Col span={12}>
                        <Form.Item label="Minimum Members" required>
                            <InputNumber
                                min={1}
                                max={1000}
                                value={groupConfig?.minMembers}
                                onChange={(value) => handleGroupConfigChange('minMembers', value)}
                                disabled={disabled}
                                placeholder="e.g., 5"
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="Maximum Members">
                            <InputNumber
                                min={1}
                                max={10000}
                                value={groupConfig?.maxMembers}
                                onChange={(value) => handleGroupConfigChange('maxMembers', value)}
                                disabled={disabled}
                                placeholder="e.g., 50"
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[16, 16]}>
                    <Col span={12}>
                        <Form.Item label="Minimum Member Age">
                            <InputNumber
                                min={16}
                                max={100}
                                value={groupConfig?.minMemberAge}
                                onChange={(value) => handleGroupConfigChange('minMemberAge', value)}
                                disabled={disabled}
                                placeholder="e.g., 18"
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[16, 16]}>
                    <Col span={12}>
                        <Form.Item label="Require Relationship" valuePropName="checked">
                            <Switch
                                checked={groupConfig?.requireRelationship || false}
                                onChange={(checked) => handleGroupConfigChange('requireRelationship', checked)}
                                disabled={disabled}
                            />
                            <Text type="secondary" style={{ marginLeft: 8 }}>
                                Members must be related
                            </Text>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="Require Same Location" valuePropName="checked">
                            <Switch
                                checked={groupConfig?.requireSameLocation || false}
                                onChange={(checked) => handleGroupConfigChange('requireSameLocation', checked)}
                                disabled={disabled}
                            />
                            <Text type="secondary" style={{ marginLeft: 8 }}>
                                Members must be from same area
                            </Text>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[16, 16]}>
                    <Col span={12}>
                        <Form.Item label="Require Constitution" valuePropName="checked">
                            <Switch
                                checked={groupConfig?.requireConstitution || false}
                                onChange={(checked) => handleGroupConfigChange('requireConstitution', checked)}
                                disabled={disabled}
                            />
                            <Text type="secondary" style={{ marginLeft: 8 }}>
                                Group must have formal constitution
                            </Text>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="Require Officials" valuePropName="checked">
                            <Switch
                                checked={groupConfig?.requireOfficials || false}
                                onChange={(checked) => handleGroupConfigChange('requireOfficials', checked)}
                                disabled={disabled}
                            />
                            <Text type="secondary" style={{ marginLeft: 8 }}>
                                Elected officials required
                            </Text>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[16, 16]}>
                    <Col span={12}>
                        <Form.Item label="Require Meeting Records" valuePropName="checked">
                            <Switch
                                checked={groupConfig?.requireMeetingRecords || false}
                                onChange={(checked) => handleGroupConfigChange('requireMeetingRecords', checked)}
                                disabled={disabled}
                            />
                            <Text type="secondary" style={{ marginLeft: 8 }}>
                                Meeting minutes/records required
                            </Text>
                        </Form.Item>
                    </Col>
                </Row>
            </Card>
            </>
        );
    }

    if (customerType === 'BUSINESS') {
        return (
            <>
                {renderUIConfigurationSection()}
                <Card>
                <Title level={4}>Business Customer Configuration</Title>
                <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                    Configure requirements for business customers, cooperatives, and corporate entities.
                </Text>

                <Title level={5}>Signatories</Title>
                <Row gutter={[16, 16]}>
                    <Col span={8}>
                        <Form.Item label="Require Signatories" valuePropName="checked">
                            <Switch
                                checked={businessConfig?.requireSignatories || false}
                                onChange={(checked) => handleBusinessConfigChange('requireSignatories', checked)}
                                disabled={disabled}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item label="Min Signatories">
                            <InputNumber
                                min={1}
                                max={20}
                                value={businessConfig?.minSignatories}
                                onChange={(value) => handleBusinessConfigChange('minSignatories', value)}
                                disabled={disabled || !businessConfig?.requireSignatories}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item label="Max Signatories">
                            <InputNumber
                                min={1}
                                max={50}
                                value={businessConfig?.maxSignatories}
                                onChange={(value) => handleBusinessConfigChange('maxSignatories', value)}
                                disabled={disabled || !businessConfig?.requireSignatories}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Title level={5}>Shareholders</Title>
                <Row gutter={[16, 16]}>
                    <Col span={12}>
                        <Form.Item label="Require Shareholders" valuePropName="checked">
                            <Switch
                                checked={businessConfig?.requireShareholders || false}
                                onChange={(checked) => handleBusinessConfigChange('requireShareholders', checked)}
                                disabled={disabled}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="Min Shareholders">
                            <InputNumber
                                min={1}
                                max={1000}
                                value={businessConfig?.minShareholders}
                                onChange={(value) => handleBusinessConfigChange('minShareholders', value)}
                                disabled={disabled || !businessConfig?.requireShareholders}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Title level={5}>Directors</Title>
                <Row gutter={[16, 16]}>
                    <Col span={12}>
                        <Form.Item label="Require Directors" valuePropName="checked">
                            <Switch
                                checked={businessConfig?.requireDirectors || false}
                                onChange={(checked) => handleBusinessConfigChange('requireDirectors', checked)}
                                disabled={disabled}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="Min Directors">
                            <InputNumber
                                min={1}
                                max={50}
                                value={businessConfig?.minDirectors}
                                onChange={(value) => handleBusinessConfigChange('minDirectors', value)}
                                disabled={disabled || !businessConfig?.requireDirectors}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Title level={5}>Documentation Requirements</Title>
                <Row gutter={[16, 16]}>
                    <Col span={12}>
                        <Form.Item label="Registration Certificate" valuePropName="checked">
                            <Switch
                                checked={businessConfig?.requireRegistrationCert || false}
                                onChange={(checked) => handleBusinessConfigChange('requireRegistrationCert', checked)}
                                disabled={disabled}
                            />
                            <Text type="secondary" style={{ marginLeft: 8 }}>
                                Business registration required
                            </Text>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="Tax ID" valuePropName="checked">
                            <Switch
                                checked={businessConfig?.requireTaxId || false}
                                onChange={(checked) => handleBusinessConfigChange('requireTaxId', checked)}
                                disabled={disabled}
                            />
                            <Text type="secondary" style={{ marginLeft: 8 }}>
                                Tax identification required
                            </Text>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[16, 16]}>
                    <Col span={12}>
                        <Form.Item label="Business License" valuePropName="checked">
                            <Switch
                                checked={businessConfig?.requireBusinessLicense || false}
                                onChange={(checked) => handleBusinessConfigChange('requireBusinessLicense', checked)}
                                disabled={disabled}
                            />
                            <Text type="secondary" style={{ marginLeft: 8 }}>
                                Operating license required
                            </Text>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="Financial Statements" valuePropName="checked">
                            <Switch
                                checked={businessConfig?.requireFinancialStatements || false}
                                onChange={(checked) => handleBusinessConfigChange('requireFinancialStatements', checked)}
                                disabled={disabled}
                            />
                            <Text type="secondary" style={{ marginLeft: 8 }}>
                                Audited financials required
                            </Text>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[16, 16]}>
                    <Col span={12}>
                        <Form.Item label="Board Resolutions" valuePropName="checked">
                            <Switch
                                checked={businessConfig?.requireBoardResolutions || false}
                                onChange={(checked) => handleBusinessConfigChange('requireBoardResolutions', checked)}
                                disabled={disabled}
                            />
                            <Text type="secondary" style={{ marginLeft: 8 }}>
                                Board resolutions for major decisions
                            </Text>
                        </Form.Item>
                    </Col>
                </Row>
            </Card>
            </>
        );
    }

    return null;
};

export default CustomerTypeConfigForm;
